import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVoice } from "./VoiceContext";
import * as apiVoice from "../../services/apiVoice";

/**
 * Browser softphone layer (Phase 1B). Owns the `@twilio/voice-sdk` `Device`
 * and the single outbound WebRTC agent leg. Mounted INSIDE `VoiceProvider`
 * (components/Ticket/index.js) so it can register an imperative handle back
 * into `VoiceContext` via `registerMediaController` — `VoiceContext` cannot
 * `useSoftphone()` because the nesting is the other way around.
 *
 * Two sources of truth, intentional: SDK events drive ONLY the local media UI
 * (mediaState / isMuted). The authoritative call lifecycle
 * (queued→ringing→active→completed) keeps arriving on the company socket in
 * `VoiceContext`. We never flip `activeCall.status` from here.
 */
export const SoftphoneContext = createContext({});

// Capability probe — runs in the real (possibly WebView) environment. Any
// failure means the device can't do WebRTC, so the caller falls back to bridge.
const detectSupported = () =>
  typeof window !== "undefined" &&
  !!window.RTCPeerConnection &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

// Classify Twilio SDK error codes into the categories the UI cares about.
const classifyError = (e) => {
  const code = (e && (e.code || (e.error && e.error.code))) || 0;
  const message = (e && (e.message || (e.error && e.error.message))) || "Softphone error";
  // Preserve the DOMException name (NotReadableError / NotAllowedError / …) —
  // it's the single most useful field for diagnosing mic-capture failures.
  const name = (e && (e.name || (e.error && e.error.name))) || "";
  return { code, message, name };
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const SoftphoneProvider = ({ children }) => {
  const { registerMediaController, handleMediaTerminated } = useVoice();
  // Latest terminal handler, read from a ref so the stable wireCall closure
  // (deps []) always invokes the current one without re-wiring SDK listeners.
  const onTerminatedRef = useRef(handleMediaTerminated);
  onTerminatedRef.current = handleMediaTerminated;

  const [supported] = useState(detectSupported);
  const [deviceState, setDeviceState] = useState("idle"); // idle|registering|ready|offline|error
  const [micPermission, setMicPermission] = useState("unknown"); // unknown|prompt|granted|denied
  const [mediaState, setMediaState] = useState("idle"); // idle|connecting|ringing|open|reconnecting|closed
  const [isMuted, setIsMuted] = useState(false);
  // Speaker (loud) vs default output. Fully effective on desktop + the native
  // iOS/Android app; on iOS Safari the OS controls routing (best-effort no-op).
  const [speakerOn, setSpeakerOn] = useState(false);
  // Mirror of speakerOn read by toggleSpeaker so repeated toggles never stick on
  // a stale closure (the callback stays identity-stable, deps []).
  const speakerOnRef = useRef(false);
  const [error, setError] = useState(null);
  // Audio device selector — list of input mics + the chosen one.
  const [availableMics, setAvailableMics] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState("");

  // Mutable refs for the SDK objects + a one-shot guard so a persistently bad
  // token can't hammer the rate-limited token endpoint.
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const readyPromiseRef = useRef(null);
  const tokenRefreshingRef = useRef(false);
  // In-flight Device build, so concurrent preflight/diagnostics/call calls share
  // ONE Device instead of each creating their own (the cause of repeated
  // register/listen + the EventEmitter MaxListeners leak).
  const deviceBuildRef = useRef(null);
  // Cached access token (+ its expiry) so preflight/diagnostics/calls REUSE one
  // token instead of hammering POST /voice/softphone/token every time.
  const tokenRef = useRef({ token: null, exp: 0 });

  const mintToken = useCallback(async (force) => {
    const now = Math.floor(Date.now() / 1000);
    // Reuse the cached token while it has >5 min left — no repeated network call.
    if (!force && tokenRef.current.token && tokenRef.current.exp - now > 300) {
      return tokenRef.current.token;
    }
    // eslint-disable-next-line no-console
    console.log("🎙️[SP] → POST /voice/softphone/token …");
    const { token } = await apiVoice.getSoftphoneToken();
    if (!token) throw new Error("ERR_VOICE_SOFTPHONE_UNAVAILABLE");
    let exp = now + 3000;
    try {
      exp = JSON.parse(atob(token.split(".")[1])).exp || exp;
    } catch (_) {
      /* keep default */
    }
    tokenRef.current = { token, exp };
    // eslint-disable-next-line no-console
    console.log("🎙️[SP] ← Access Token received ✓ (cached)");
    return token;
  }, []);

  // Non-throwing mic preflight — "check microphone" before a call. Returns
  // { ok, message } (with the exact browser error name) and releases the stream.
  // List the input microphones the browser sees (labels appear once permission
  // has been granted at least once).
  const listMics = useCallback(async () => {
    if (!supported || !navigator.mediaDevices.enumerateDevices) return [];
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const mics = devs.filter((d) => d.kind === "audioinput");
      setAvailableMics(mics);
      return mics;
    } catch (_) {
      return [];
    }
  }, [supported]);

  const probeMic = useCallback(
    async (deviceId) => {
      if (!supported)
        return { ok: false, message: "הדפדפן/המכשיר לא תומך ב-WebRTC (getUserMedia חסר)" };
      const constraints = deviceId
        ? { audio: { deviceId: { exact: deviceId } } }
        : { audio: true };
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        stream.getTracks().forEach((t) => t.stop());
        setMicPermission("granted");
        // Now that permission is granted, labels are available — refresh the list.
        listMics();
        return { ok: true, message: "מיקרופון תקין ✓" };
      } catch (err0) {
        // If a SPECIFIC mic was requested and failed, the hardware may still be
        // fine — retry with the system default. If THAT works, the selected
        // device is stale; reset it and tell the user (not a hardware failure).
        if (deviceId && (err0.name === "OverconstrainedError" || err0.name === "NotReadableError" || err0.name === "NotFoundError")) {
          try {
            const s2 = await navigator.mediaDevices.getUserMedia({ audio: true });
            s2.getTracks().forEach((t) => t.stop());
            setMicPermission("granted");
            setSelectedMicId(""); // fall back to system default
            listMics();
            return { ok: true, message: "המיקרופון שנבחר לא זמין — עברנו לברירת מחדל ✓" };
          } catch (_) {
            /* default also failed — report the original error below */
          }
        }
        const err = err0;
        const name = (err && err.name) || "Error";
        let msg;
        if (name === "NotAllowedError" || name === "SecurityError") {
          setMicPermission("denied");
          msg = "הרשאת מיקרופון נדחתה — אשר/י לאתר (🔒 בשורת הכתובת)";
        } else if (name === "NotReadableError") {
          setMicPermission("prompt");
          // Permission exists but the OS couldn't open the device.
          msg = "המיקרופון מזוהה אך תפוס/לא זמין במערכת ההפעלה — סגור Zoom/WhatsApp/Teams ונסה שוב";
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setMicPermission("prompt");
          msg = "לא נמצא מיקרופון זמין";
        } else {
          setMicPermission("prompt");
          msg = "שגיאת מיקרופון";
        }
        return { ok: false, message: `${msg} [${name}]` };
      }
    },
    [supported, listMics]
  );

  // ── SDK call-event wiring (per spec §2.2) ────────────────────────────────
  const wireCall = useCallback((call) => {
    callRef.current = call;
    setMediaState("connecting");
    setIsMuted(false);

    call.on("ringing", () => setMediaState("ringing")); // UI hint only
    // accept = agent mic is LIVE (NOT "customer answered"); status truth is the
    // server's "active" over the socket.
    call.on("accept", () => setMediaState("open"));
    call.on("reconnecting", () => setMediaState("reconnecting"));
    call.on("reconnected", () => setMediaState("open"));
    call.on("mute", (m) => setIsMuted(!!m));
    call.on("error", (e) => setError(classifyError(e)));

    const onTerminal = () => {
      setMediaState("closed");
      callRef.current = null;
      setIsMuted(false);
      // The WebRTC leg ended (agent hung up, customer hung up, or media drop).
      // Authoritatively end the call in VoiceContext + server so the call bar
      // timer stops and the customer leg is torn down. Idempotent with the UI
      // hang-up button (server endCall is safe to call twice).
      try {
        if (onTerminatedRef.current) onTerminatedRef.current();
      } catch (_) {
        /* noop */
      }
    };
    call.on("disconnect", onTerminal);
    call.on("cancel", onTerminal);
  }, []);

  // ── Device lifecycle ─────────────────────────────────────────────────────
  const buildDevice = useCallback(
    async (token) => {
      // Lazy import keeps the SDK out of the main bundle until softphone is used.
      const { Device } = await import("@twilio/voice-sdk");
      const device = new Device(token, {
        codecPreferences: ["opus", "pcmu"],
        logLevel: 1,
      });
      // The SDK is an EventEmitter; we attach a handful of listeners (and the
      // app may rebuild once). Raise the cap so we never hit the spurious
      // MaxListenersExceededWarning.
      try {
        if (typeof device.setMaxListeners === "function") device.setMaxListeners(30);
      } catch (_) {
        /* noop */
      }
      // eslint-disable-next-line no-console
      console.log("🎙️[SP] Twilio Device created (single instance)");

      device.on("registered", () => {
        setDeviceState("ready");
        setError(null);
      });
      device.on("unregistered", () => setDeviceState("offline"));
      device.on("error", (e) => {
        const classified = classifyError(e);
        setError(classified);
        setDeviceState("error");
        if (classified.code === 31401) setMicPermission("denied");
      });
      // Re-mint before expiry so an in-progress call is NOT dropped.
      device.on("tokenWillExpire", async () => {
        if (tokenRefreshingRef.current) return;
        tokenRefreshingRef.current = true;
        try {
          const fresh = await mintToken();
          device.updateToken(fresh);
        } catch (err) {
          setError(classifyError(err));
        } finally {
          tokenRefreshingRef.current = false;
        }
      });

      return device;
    },
    [mintToken]
  );

  // Idempotent registration. Twilio throws if you call register() when the
  // device is already "registered", so we branch on device.state. A destroyed
  // device is rebuilt. This is what makes the preflight runnable 10x in a row
  // without "Attempt to register when device is in state registered".
  const ensureRegistered = useCallback(
    async (token) => {
      let device = deviceRef.current;
      if (device && device.state === "destroyed") {
        deviceRef.current = null;
        device = null;
      }
      // Already registered → reuse as-is. No updateToken, no re-register: this is
      // what stops the repeated token/listen/register churn.
      if (device && device.state === "registered") return { device, status: "already" };
      if (!device) {
        // Share a single in-flight build across concurrent callers.
        if (!deviceBuildRef.current) {
          deviceBuildRef.current = buildDevice(token)
            .then((d) => {
              deviceRef.current = d;
              return d;
            })
            .finally(() => {
              deviceBuildRef.current = null;
            });
        }
        device = await deviceBuildRef.current;
      } else {
        try {
          device.updateToken(token);
        } catch (_) {
          /* token swap best-effort */
        }
      }
      const st = device.state;
      if (st === "registered") return { device, status: "already" };
      if (st === "registering") return { device, status: "registering" };
      await device.register(); // only from "unregistered"
      return { device, status: "registered" };
    },
    [buildDevice]
  );

  // Warm the Device: token + register ONLY. We deliberately DO NOT open the mic
  // here. device.connect() opens the mic exactly once — pre-capturing it (the
  // old requestMic) and stopping it microseconds before connect re-acquired it
  // caused the AcquisitionFailedError (31402) / NotReadableError race on Windows
  // (the device wasn't released yet). Single capture = no race.
  const ensureReady = useCallback(async () => {
    if (!supported) throw new Error("WEBRTC_UNSUPPORTED");
    // Reuse a Device that's already registered with the SDK (real state, not our
    // lagging deviceState) — avoids re-mint / re-register on every call.
    const dev = deviceRef.current;
    if (dev && dev.state === "registered") return;
    if (readyPromiseRef.current) return readyPromiseRef.current;

    const tag = (e, stage) =>
      Object.assign(e instanceof Error ? e : new Error(String(e)), { stage });

    const run = (async () => {
      setError(null);
      setDeviceState("registering");
      let token;
      try {
        token = await mintToken();
      } catch (e) {
        throw tag(e, "token");
      }
      try {
        await ensureRegistered(token); // idempotent — never double-registers
      } catch (e) {
        throw tag(e, "register");
      }
    })();

    readyPromiseRef.current = run;
    try {
      await run;
    } catch (err) {
      setDeviceState("error");
      setError(classifyError(err));
      throw err;
    } finally {
      readyPromiseRef.current = null;
    }
  }, [supported, mintToken, ensureRegistered]);

  // FULL browser preflight — proves exactly where the softphone chain stops in
  // THIS browser: mic (getUserMedia) → token (network) → Device.register
  // (Twilio signaling). token + register do NOT need the mic, so they run even
  // if the mic fails. Defined AFTER buildDevice/ensureReady to avoid a TDZ.
  const runPreflight = useCallback(async () => {
    const r = { mic: null, token: null, register: null };
    r.mic = await probeMic(selectedMicId || undefined);
    try {
      const token = await mintToken();
      r.token = { ok: !!token, message: token ? "Access Token התקבל ✓" : "לא התקבל טוקן" };
      try {
        const { status } = await ensureRegistered(token);
        const msg =
          status === "already"
            ? "Twilio Device כבר רשום ✓"
            : status === "registering"
              ? "Twilio Device בתהליך רישום…"
              : "Twilio Device נרשם ✓";
        r.register = { ok: true, message: msg };
      } catch (e) {
        const c = (e && (e.code || e.message)) || "error";
        r.register = { ok: false, message: `רישום Device נכשל [${c}]` };
      }
    } catch (e) {
      r.token = { ok: false, message: `טוקן נכשל: ${(e && e.message) || "error"}` };
      r.register = { ok: false, message: "לא נבדק (אין טוקן)" };
    }
    return r;
  }, [probeMic, mintToken, ensureRegistered, selectedMicId]);

  // ADVANCED AUDIO DIAGNOSTICS — exercises the EXACT path and pins the culprit:
  // enumerate → getUserMedia(default) → getUserMedia(selected) → track state →
  // Twilio setInputDevice. Returns { steps:[{label,ok,detail}], verdict }.
  const runAudioDiagnostics = useCallback(async () => {
    const steps = [];
    const add = (label, ok, detail) => steps.push({ label, ok, detail });
    if (!supported) {
      return { steps: [{ label: "תמיכה", ok: false, detail: "WebRTC לא נתמך" }], verdict: "הדפדפן/המכשיר לא תומך ב-WebRTC." };
    }
    // enumerate
    let mics = [];
    try {
      mics = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "audioinput");
    } catch (_) {
      /* ignore */
    }
    const labeled = mics.filter((m) => m.label).length;
    add("רשימת מיקרופונים", mics.length > 0, `${mics.length} התקנים · ${labeled} עם שם${labeled === 0 ? " (אין הרשאה עדיין)" : ""}`);
    const sel = selectedMicId ? (mics.find((m) => m.deviceId === selectedMicId) || {}).label || "נבחר" : "ברירת מחדל";
    add("מיקרופון נבחר", true, `${sel}${selectedMicId ? ` [${selectedMicId.slice(0, 8)}…]` : ""}`);
    // getUserMedia default
    let s3ok = false;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const t = s.getAudioTracks()[0];
      let d = `track=${t ? t.readyState : "-"} · ${t ? t.label : ""}`;
      await sleep(400);
      d += ` · אחרי 400ms=${t ? t.readyState : "-"}`;
      s.getTracks().forEach((x) => x.stop());
      d += ` · אחרי stop=${t ? t.readyState : "-"}`;
      s3ok = true;
      add("getUserMedia (ברירת מחדל)", true, d);
    } catch (e) {
      add("getUserMedia (ברירת מחדל)", false, `נכשל [${(e && e.name) || "Error"}]`);
    }
    // getUserMedia selected
    let s4ok = null;
    if (selectedMicId) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: selectedMicId } } });
        s.getTracks().forEach((x) => x.stop());
        s4ok = true;
        add("getUserMedia (נבחר)", true, "נפתח ✓");
      } catch (e) {
        s4ok = false;
        add("getUserMedia (נבחר)", false, `נכשל [${(e && e.name) || "Error"}]`);
      }
    }
    // Twilio Device readiness — register only (no setInputDevice churn; the call
    // lets device.connect open the default mic itself).
    let s8ok = null;
    try {
      const token = await mintToken();
      const { device } = await ensureRegistered(token);
      const n =
        device.audio && device.audio.availableInputDevices
          ? device.audio.availableInputDevices.size
          : 0;
      s8ok = true;
      add("Twilio Device", true, `רשום ✓ · ${n} התקני קלט ל-SDK`);
    } catch (e) {
      s8ok = false;
      add("Twilio Device", false, `נכשל [${(e && (e.name || e.message)) || "Error"}]`);
    }
    // verdict
    let verdict;
    if (s3ok && s4ok === false) verdict = "ה-deviceId שנבחר תקול/לא זמין — עבור ל'ברירת מחדל' או בחר מיקרופון אחר.";
    else if (!s3ok) verdict = "הדפדפן/Windows לא מצליח לפתוח את המיקרופון — exclusivity / אפליקציה אחרת / דרייבר. כבה 'Allow exclusive control' וסגור אפליקציות שמשתמשות במיקרופון.";
    else if (s3ok && s8ok === false) verdict = "getUserMedia עובד אך Twilio נכשל — בעיית אינטגרציה (constraint / שחרור התקן).";
    else verdict = "האודיו תקין מקצה לקצה ✓ — אפשר לחייג.";
    return { steps, verdict };
  }, [supported, selectedMicId, mintToken, ensureRegistered]);

  // ── Place / mute / hangup (the imperative handle surface) ─────────────────
  const placeBrowserCall = useCallback(
    async ({ callId }) => {
      await ensureReady();
      const device = deviceRef.current;
      if (!device) throw Object.assign(new Error("DEVICE_NOT_READY"), { stage: "register" });
      // POC: do NOT call setInputDevice before connect. We let device.connect()
      // open the DEFAULT microphone itself, exactly once. Pre-setting the input
      // device (default/communications/deviceId) churned the AudioHelper and
      // raced the connect capture. Clean single acquisition.
      // ONLY callSessionId leaves the client — the customer number + callerId are
      // resolved server-side from the CallSession in the TwiML handler (§11).
      let call;
      try {
        // eslint-disable-next-line no-console
        console.log(`🎙️[SP] → device.connect({callSessionId:${callId}}) — placing the WebRTC agent leg (Twilio will dial the customer via TwiML)`);
        call = await device.connect({
          params: { callSessionId: String(callId) },
        });
        // eslint-disable-next-line no-console
        console.log("🎙️[SP] device.connect resolved — WebRTC leg up");
      } catch (e) {
        throw Object.assign(e instanceof Error ? e : new Error(String(e)), {
          stage: "connect",
        });
      }
      wireCall(call);
    },
    [ensureReady, wireCall]
  );

  // Mute/unmute. The target is derived from the SDK's OWN current mute state
  // (call.isMuted()), not from React state — so repeated toggles never get stuck
  // on a stale closure. Falls back to the disabled-track approach if needed.
  const mute = useCallback((next) => {
    const call = callRef.current;
    if (!call) return;
    let current = isMuted;
    try {
      if (typeof call.isMuted === "function") current = !!call.isMuted();
    } catch (_) {
      /* use react state */
    }
    const target = typeof next === "boolean" ? next : !current;
    try {
      call.mute(target);
    } catch (_) {
      /* noop */
    }
    setIsMuted(target);
  }, [isMuted]);

  // Toggle audio output between the default device and "speaker" (all available
  // outputs). Best-effort via the Twilio Device's OutputDeviceCollection — works
  // on desktop and the native app; iOS Safari ignores it (OS-routed). Never
  // throws into the UI.
  const toggleSpeaker = useCallback((next) => {
    const device = deviceRef.current;
    const on = typeof next === "boolean" ? next : !speakerOnRef.current;
    speakerOnRef.current = on;
    setSpeakerOn(on);
    try {
      const audio = device && device.audio;
      if (audio && audio.speakerDevices && typeof audio.speakerDevices.set === "function") {
        const outs = audio.availableOutputDevices;
        if (on && outs && outs.size) {
          audio.speakerDevices.set(Array.from(outs.keys()));
        } else {
          audio.speakerDevices.set("default");
        }
      }
    } catch (_) {
      /* best-effort: native app handles real routing */
    }
  }, []);

  const hangup = useCallback(() => {
    const call = callRef.current;
    if (call) {
      try {
        call.disconnect();
      } catch (_) {
        /* already gone */
      }
    }
    callRef.current = null;
    setMediaState("closed");
    setIsMuted(false);
    setSpeakerOn(false);
    speakerOnRef.current = false;
  }, []);

  // ── Register the imperative handle into VoiceContext ──────────────────────
  // Keep stable identities so VoiceContext always calls the latest closures.
  const ensureReadyRef = useRef(ensureReady);
  const placeRef = useRef(placeBrowserCall);
  const muteRef = useRef(mute);
  const hangupRef = useRef(hangup);
  ensureReadyRef.current = ensureReady;
  placeRef.current = placeBrowserCall;
  muteRef.current = mute;
  hangupRef.current = hangup;

  useEffect(() => {
    if (typeof registerMediaController !== "function") return undefined;
    registerMediaController({
      supported,
      ensureReady: (...a) => ensureReadyRef.current(...a),
      placeBrowserCall: (...a) => placeRef.current(...a),
      mute: (...a) => muteRef.current(...a),
      hangup: (...a) => hangupRef.current(...a),
    });
    return () => registerMediaController(null);
  }, [registerMediaController, supported]);

  // Tear the Device down on unmount.
  useEffect(() => {
    return () => {
      try {
        if (callRef.current) callRef.current.disconnect();
      } catch (_) {
        /* noop */
      }
      try {
        if (deviceRef.current) deviceRef.current.destroy();
      } catch (_) {
        /* noop */
      }
      deviceRef.current = null;
      callRef.current = null;
    };
  }, []);

  const value = useMemo(
    () => ({
      supported,
      deviceState,
      micPermission,
      mediaState,
      isMuted,
      speakerOn,
      toggleSpeaker,
      error,
      ensureReady,
      placeBrowserCall,
      mute,
      hangup,
      probeMic,
      runPreflight,
      runAudioDiagnostics,
      listMics,
      availableMics,
      selectedMicId,
      setSelectedMicId,
    }),
    [
      supported,
      deviceState,
      micPermission,
      mediaState,
      isMuted,
      speakerOn,
      toggleSpeaker,
      error,
      ensureReady,
      placeBrowserCall,
      mute,
      hangup,
      probeMic,
      runPreflight,
      runAudioDiagnostics,
      listMics,
      availableMics,
      selectedMicId,
    ]
  );

  return (
    <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>
  );
};

/**
 * Hard isolation boundary: if SoftphoneProvider ever throws during render, the
 * Ticket MUST survive. On failure this renders the children WITHOUT the provider
 * (useSoftphone() then returns the default {} — every consumer already handles
 * that and the call button silently uses the phone bridge). A Voice/Softphone
 * bug therefore can NEVER take down the Ticket / messages / contacts / CRM.
 */
export class SafeSoftphoneProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.error("[Softphone] disabled after a render error (CRM unaffected):", err);
  }

  render() {
    if (this.state.failed) return this.props.children;
    return <SoftphoneProvider>{this.props.children}</SoftphoneProvider>;
  }
}

export const useSoftphone = () => useContext(SoftphoneContext);
